import get from 'lodash/get'
import store from '@/store/store'
import format from 'date-fns/format'
import widgetDataTypes from '@/enum/widgetDataTypes'
import { queueActivityGaugeKey } from '@/enum/generic'
import { realTimeTableKey } from '@/enum/realTimeTableConfigs'
import {
    endOfDay,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
    subYears
} from 'date-fns'

// minimum refresh interval for real-time widgets - 10 seconds
const MIN_REFRESH_INTERVAL = 10
const MULTI_QUEUES_DASHBOARD_KEY = 'GetMultiQueuesDashboard'
const MULTI_QUEUE_RULES_DASHBOARD_KEY = 'GetMultiQueuesDashboardRule'
let refreshIntervalLocal = 0

const CHART_DATA_TYPE_IDS = [
    widgetDataTypes.LINES_TYPE_ID,
    widgetDataTypes.BARS_WITH_LINES_TYPE_ID,
    widgetDataTypes.X_AXIS_TYPE_ID,
    widgetDataTypes.CHART_QUEUE,
    widgetDataTypes.PIE_TYPE_ID,
    widgetDataTypes.CHART_AREA_ID,
]

const minRefreshInterval = () => {
    return store.getters['layout/minRefreshInterval']('activeLayout') || MIN_REFRESH_INTERVAL
}

export function getWidgetRefreshInterval(widget) {
    let widgetRefreshInterval = get(widget, 'WidgetLayout.DefaultRefreshInterval')
    let timeToLoadData = 0
    if (!widgetRefreshInterval) {
        let widgetTemplate = getWidgetTemplate(widget)
        widgetRefreshInterval = get(widgetTemplate, 'DefaultRefreshInterval', '')
    }

    let minInterval = minRefreshInterval()
    widgetRefreshInterval = Number(widgetRefreshInterval) <= minInterval ? minInterval : Number(widgetRefreshInterval)

    const widgetsStoreData = store.getters['axiosRequestsDuration/getRequestsInfo']
    const widgetStoreData = widgetsStoreData.find(el => el.widgetId === widget.WidgetID)
    if (widgetStoreData) {
        timeToLoadData = widgetStoreData.duration
    }

    refreshIntervalLocal = timeToLoadData && refreshIntervalLocal < timeToLoadData && widgetRefreshInterval < timeToLoadData ? timeToLoadData : widgetRefreshInterval

    return refreshIntervalLocal * 1000
}

export function getWidgetEndpoint(widget) {
    let endpoint = get(widget, 'WidgetLayout.Endpoint')
    if (!endpoint) {
        let widgetTemplate = getWidgetTemplate(widget)
        endpoint = get(widgetTemplate, 'Endpoint', '')
    }
    return endpoint.replace('{WidgetID}', widget.WidgetID)
}

export function getWidgetDataType(widget) {
    let dataType = get(widget, 'WidgetLayout.DataTypeID')
    if (!dataType) {
        let widgetTemplate = getWidgetTemplate(widget)
        dataType = get(widgetTemplate, 'DataType.DataTypeID')
    }
    return dataType
}

export function getWidgetTemplate(widget) {
    let getWidgetTemplate = store.getters['templatesCategory/getWidgetTemplate']
    return getWidgetTemplate(widget.TemplateID)
}

export const componentWidth = {
    [widgetDataTypes.COUNTER_TYPE_ID]: 'lg:w-auto',
    [widgetDataTypes.QUEUE_COUNTER_TYPE_ID]: 'lg:w-auto',
    [widgetDataTypes.TOTAL_OUTGOING_CALLS]: 'lg:w-auto',
    [widgetDataTypes.PIE_TYPE_ID]: '',
    default: 'lg:w-3/3',
}

export function getDataTypeClass(widget) {
    let dataType = 'default'
    try {
        dataType = getWidgetDataType(widget)
    } catch (e) {
        console.warn(e)
    }
    return componentWidth[dataType]
}

export function isRealtimeWidget(widget) {
    if (widget.EndPoint.includes(realTimeTableKey)) {
        return true
    } else if (widget.DataTypeID === widgetDataTypes.EXTENSION_CARDS) {
        return true
    }
    return false
}

export function isMultiQueuesDashboard(widget) {
    return !isMultiQueueRulesDashboard(widget) && widget.EndPoint.includes(MULTI_QUEUES_DASHBOARD_KEY)
}

export function isMultiQueueRulesDashboard(widget) {
    return widget.EndPoint.includes(MULTI_QUEUE_RULES_DASHBOARD_KEY)
}

export function isPieWidget(widget) {
    return widget.DataTypeID === widgetDataTypes.PIE_TYPE_ID || widget.WidgetLayout.ComponentTypeID === widgetDataTypes.PIE_TYPE_ID
}

export function isExternalDataWidget(widget) {
    return widget.DataTypeID === widgetDataTypes.EXTERNAL_DATA_TYPE_ID
}

export function isQueueTable(widget) {
    return widget.DataTypeID === widgetDataTypes.QUEUE_ACTIVE_CALL
}

export function isTableWidget(widget) {
    return widget.DataTypeID === widgetDataTypes.TABLE_TYPE_ID
}

export function isQueueGauge(widget) {
    return widget.DataTypeID === widgetDataTypes.CHART_SPEEDOMETER
}

export function isQueueChart(widget) {
    return widget.DataTypeID === widgetDataTypes.CHART_QUEUE
}

export function isQueueDashboardWidget(widget) {
    return widget.DataTypeID === widgetDataTypes.QUEUE_DASHBOARD && !isQueueActivityGauge(widget)
}

export function isQueueActivityGauge(widget) {
    return widget.EndPoint.includes(queueActivityGaugeKey)
}

export function isAreaChartWidget(widget) {
    return widget.DataTypeID === widgetDataTypes.CHART_AREA_ID
}

export function isHtmlEditor(widget) {
    return widget.DataTypeID === widgetDataTypes.RICH_TEXT_EDITOR_ID
}

export function isExtensionCardStatisticsWidget (widget) {
    return widget.DataTypeID === widgetDataTypes.EXTENSION_CARDS_STATISTICS
}

export function isNoteListWidget(widget) {
    return widget.DataTypeID === widgetDataTypes.RICH_TEXT_EDITOR_LIST
}

export function isARealtimeTableWidget(widget) {
    let isQueueWidgetType = [widgetDataTypes.QUEUE_DASHBOARD, widgetDataTypes.QUEUE_ACTIVE_CALL].includes(widget.DataTypeID)
    return isQueueWidgetType || isRealtimeWidget(widget) || isMultiQueuesDashboard(widget)
}

export function timeFilterToHuman(widget, toFormat = 'dd/MM/yyyy') {
    let start = new Date()
    let end = new Date()

    const startDate = get(widget, 'WidgetTime.Date_interval', 0)
    const dateInterval = get(widget, 'WidgetTime.datedeff', 0)

    start.setDate(start.getDate() - startDate)
    end.setDate(end.getDate() - (startDate - dateInterval))

    return `${format(start, toFormat)}  -  ${format(end, toFormat)}`
}

export function timeFilterForPeriod (typeOfPeriod = 'quarter', periodsBack = 0, toFormat = 'dd/MM/yyyy', calendaric = false) {
    const { start, end } = getPeriodDates(typeOfPeriod, periodsBack, calendaric)

    return `${format(start, toFormat)}  -  ${format(end, toFormat)}`
}

function getPeriodDates (periodType, periodsBack, calendaric) {
    const today = new Date()
    let start, end

    const periodFunctions = {
        day: {
            startOfPeriod: startOfDay,
            subPeriods: subDays,
            endOfPeriod: endOfDay,
            adjustDate: (date, periodsBack) => date.setDate(date.getDate() - periodsBack),
        },
        week: {
            startOfPeriod: (date) => startOfWeek(date, { weekStartsOn: 1 }),
            subPeriods: subWeeks,
            endOfPeriod: (date) => endOfWeek(date, { weekStartsOn: 1 }),
            adjustDate: (date, periodsBack) => date.setDate(date.getDate() - periodsBack * 7),
        },
        month: {
            startOfPeriod: startOfMonth,
            subPeriods: subMonths,
            endOfPeriod: endOfMonth,
            adjustDate: (date, periodsBack) => date.setMonth(date.getMonth() - periodsBack),
        },
        year: {
            startOfPeriod: startOfYear,
            subPeriods: subYears,
            endOfPeriod: endOfYear,
            adjustDate: (date, periodsBack) => date.setFullYear(date.getFullYear() - periodsBack),
        },
        quarter: {
            startOfPeriod: startOfQuarter,
            subPeriods: subQuarters,
            endOfPeriod: endOfQuarter,
            adjustDate: (date, periodsBack) => date.setMonth(date.getMonth() - periodsBack * 3),
        },
    }

    const periodFuncs = periodFunctions[periodType] || periodFunctions.quarter

    if (!calendaric) {
        start = new Date(today)
        end = today
        periodFuncs.adjustDate(start, periodsBack)
    } else {
        const currentPeriodStart = periodFuncs.startOfPeriod(today)
        start = periodFuncs.subPeriods(currentPeriodStart, periodsBack)
        const lastPeriodStart = periodFuncs.subPeriods(currentPeriodStart, 1)
        end = periodFuncs.endOfPeriod(lastPeriodStart)
    }

    return {
        start,
        end
    }
}

export function timeFilterForCalendricalPeriod(widget, toFormat = 'dd/MM/yyyy') {
    const start = get(widget, 'WidgetDateInterval.StartDate', '')
    const end = get(widget, 'WidgetDateInterval.EndDate', '')

    return `${format(new Date(start), toFormat)}  -  ${format(new Date(end), toFormat)}`
}

export function isChartWidget(widget) {
    return CHART_DATA_TYPE_IDS.includes(widget.DataTypeID) || isQueueActivityGauge(widget) || isPieWidget(widget) || isQueueGauge(widget) || isFunnelChartWidget(widget)
}

export function isCounterAgentsInStatus(widget) {
    return widget.DataTypeID === widgetDataTypes.COUNTER_TYPE_ID
}

export function isFunnelChartWidget(widget) {
    return widget.DataTypeID === widgetDataTypes.INFO_TYPE_ID
}

export function isSocketsRealTimeTableWidget (widget) {
    return widget.DataTypeID === widgetDataTypes.DIALERS_TYPE_ID
}

export function getWidgetParameterConfig (widgetTemplate, parameterId, keysToInclude = []) {
    let result = {}

    if (widgetTemplate && widgetTemplate.DefaultWidgetConfig) {
        const currentWidgetParameterDefaultConfig = widgetTemplate.DefaultWidgetConfig.find((el) => el.ParameterID === parameterId)

        if (currentWidgetParameterDefaultConfig) {
            result = { ...currentWidgetParameterDefaultConfig }

            if (keysToInclude.length) {
                result = keysToInclude.reduce
                ((acc, key) => {
                        if (currentWidgetParameterDefaultConfig[key]) {
                            acc[key] = currentWidgetParameterDefaultConfig[key]
                        }

                        return acc
                    },
                    {}
                )
            }
        }
    }

    return result
}
